import { questionService, createQuestionFormData } from '@/services/questionsApi'

const state = {
  questions: [],
  currentQuestion: null,
  loading: false,
  error: null,
  filters: {
    area: null,
    author: null,
    difficulty: null
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  },
  userAnswer: null,
  answerResult: null
}

const getters = {
  questions: (state) => state.questions,
  currentQuestion: (state, getters, rootState) => {
    const userRole = rootState.auth.user?.role
    const question = state.currentQuestion

    if (!question) return null

    // La nueva API ya devuelve la estructura normalizada con opciones como array
    let processedQuestion = { ...question }

    // Asegurar que las opciones existen
    if (!processedQuestion.options || !Array.isArray(processedQuestion.options)) {
      processedQuestion.options = []
    }

    // Para estudiantes, ocultar justification y filtrar is_correct de las opciones
    if (userRole === 'student') {
      const { justification, ...publicQuestion } = processedQuestion
      // Filtrar is_correct de las opciones para estudiantes
      publicQuestion.options = publicQuestion.options.map(option => {
        const { is_correct, ...publicOption } = option
        return publicOption
      })
      return publicQuestion
    }

    // Para profesores y admins, mostrar todo
    return processedQuestion
  },
  loading: (state) => state.loading,
  error: (state) => state.error,
  filters: (state) => state.filters,
  pagination: (state) => state.pagination,
  userAnswer: (state) => state.userAnswer,
  answerResult: (state) => state.answerResult,
  
  // Filtrar preguntas según rol del usuario
  filteredQuestions: (state, getters, rootState) => {
    const userRole = rootState.auth.user?.role

    return state.questions.map(question => {
      let processedQuestion = { ...question }

      // Asegurar que las opciones existen
      if (!processedQuestion.options || !Array.isArray(processedQuestion.options)) {
        processedQuestion.options = []
      }

      // Para estudiantes, ocultar justification y filtrar is_correct de las opciones
      if (userRole === 'student') {
        const { justification, ...publicQuestion } = processedQuestion
        // Filtrar is_correct de las opciones para estudiantes
        publicQuestion.options = publicQuestion.options.map(option => {
          const { is_correct, ...publicOption } = option
          return publicOption
        })
        return publicQuestion
      }

      // Para profesores y admins, mostrar todo
      return processedQuestion
    })
  }
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  
  SET_ERROR(state, error) {
    state.error = error
  },
  
  CLEAR_ERROR(state) {
    state.error = null
  },
  
  SET_QUESTIONS(state, questions) {
    state.questions = questions
  },
  
  SET_CURRENT_QUESTION(state, question) {
    state.currentQuestion = question
  },
  
  ADD_QUESTION(state, question) {
    state.questions.unshift(question)
  },
  
  UPDATE_QUESTION(state, updatedQuestion) {
    const index = state.questions.findIndex(q => q.id === updatedQuestion.id)
    if (index !== -1) {
      state.questions.splice(index, 1, updatedQuestion)
    }
  },
  
  REMOVE_QUESTION(state, questionId) {
    state.questions = state.questions.filter(q => q.id !== questionId)
  },
  
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },
  
  RESET_FILTERS(state) {
    state.filters = {
      area: null,
      author: null,
      difficulty: null
    }
  },
  
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination }
  },
  
  SET_USER_ANSWER(state, answer) {
    state.userAnswer = answer
  },
  
  SET_ANSWER_RESULT(state, result) {
    state.answerResult = result
  },
  
  CLEAR_ANSWER_DATA(state) {
    state.userAnswer = null
    state.answerResult = null
  }
}

const actions = {
  async fetchQuestions({ commit, state }, customParams = {}) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const params = {
        page: customParams.page || state.pagination.page,
        limit: customParams.limit || state.pagination.limit,
        ...state.filters,
        ...customParams
      }

      // Remover parámetros null o undefined
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) {
          delete params[key]
        }
      })

      const response = await questionService.getQuestions(params)

      const questions = response.data || response
      const meta = response.meta || {}

      commit('SET_QUESTIONS', questions)
      if (meta) {
        commit('SET_PAGINATION', {
          total: meta.total,
          totalPages: meta.last_page || meta.total_pages,
          page: meta.current_page
        })
      }

      return {
        success: true,
        data: {
          questions,
          totalPages: meta.last_page || meta.total_pages || 1,
          total: meta.total || questions.length,
          currentPage: meta.current_page || params.page
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error al cargar preguntas'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchQuestionById({ commit }, questionId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await questionService.getQuestion(questionId)

      const question = response.data || response
      commit('SET_CURRENT_QUESTION', question)

      return { success: true, data: question, question }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error al cargar la pregunta'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async createQuestion({ commit }, questionData) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      let requestData
      let hasFiles = false

      // Determinar si hay archivos (imagen del enunciado, justificación o de opciones)
      hasFiles = !!questionData.statement_image ||
                 !!questionData.justification_image ||
                 questionData.options.some(option => option.image)

      if (hasFiles) {
        // Usar FormData para archivos
        requestData = createQuestionFormData(questionData)
      } else {
        // Usar JSON para datos sin archivos
        requestData = {
          statement: questionData.statement,
          justification: questionData.justification,
          area: questionData.area,
          time_limit: questionData.time_limit,
          school_grade_id: questionData.school_grade_id,
          school_subject_id: questionData.school_subject_id,
          options: questionData.options.map(option => ({
            ...option,
            is_correct: option.is_correct ? 1 : 0 // Convert to 1/0 for backend
          }))
        }
      }

      const response = await questionService.createQuestion(requestData, hasFiles)

      const newQuestion = response.data || response
      commit('ADD_QUESTION', newQuestion)

      return { success: true, question: newQuestion }
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.errors?.options?.[0] ||
                          error.message || 'Error al crear la pregunta'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async updateQuestion({ commit }, { questionId, questionData }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      let requestData
      let hasFiles = false

      // Determinar si hay archivos nuevos
      hasFiles = !!questionData.statement_image ||
                 !!questionData.justification_image ||
                 questionData.options.some(option => option.image)

      if (hasFiles) {
        // Usar FormData para archivos
        requestData = createQuestionFormData(questionData)
      } else {
        // Usar JSON para actualización sin archivos
        requestData = {
          statement: questionData.statement,
          justification: questionData.justification,
          area: questionData.area,
          time_limit: questionData.time_limit,
          school_grade_id: questionData.school_grade_id,
          school_subject_id: questionData.school_subject_id,
          options: questionData.options.map(option => ({
            ...option,
            is_correct: option.is_correct ? 1 : 0 // Convert to 1/0 for backend
          }))
        }
      }

      const response = await questionService.updateQuestion(questionId, requestData, hasFiles)

      const updatedQuestion = response.data || response
      commit('UPDATE_QUESTION', updatedQuestion)

      return { success: true, question: updatedQuestion }
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.errors?.options?.[0] ||
                          error.message || 'Error al actualizar la pregunta'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async deleteQuestion({ commit }, questionId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      await questionService.deleteQuestion(questionId)
      
      commit('REMOVE_QUESTION', questionId)
      
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error al eliminar la pregunta'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async answerQuestion({ commit }, { questionId, selectedOptionId, timeSpent }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await questionService.answerQuestion(questionId, {
        selected_option_id: selectedOptionId,
        time_taken: timeSpent
      })

      const result = response.data || response

      commit('SET_USER_ANSWER', selectedOptionId)
      commit('SET_ANSWER_RESULT', result)

      return { success: true, result }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error al enviar la respuesta'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  setFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters)
    // Reiniciar paginación al aplicar filtros
    commit('SET_PAGINATION', { page: 1 })
    // Recargar preguntas con nuevos filtros
    return dispatch('fetchQuestions')
  },
  
  resetFilters({ commit, dispatch }) {
    commit('RESET_FILTERS')
    commit('SET_PAGINATION', { page: 1 })
    return dispatch('fetchQuestions')
  },
  
  changePage({ commit, dispatch }, page) {
    commit('SET_PAGINATION', { page })
    return dispatch('fetchQuestions')
  },
  
  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },
  
  clearAnswerData({ commit }) {
    commit('CLEAR_ANSWER_DATA')
  },

  async fetchQuestionsBySchoolGrade({ commit }, { schoolGradeId, pinCode = null }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await questionService.getQuestionsBySchoolGrade(schoolGradeId, pinCode)

      const questions = response.data || response
      commit('SET_QUESTIONS', questions)

      return { success: true, data: questions }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error al cargar preguntas del grado escolar'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}